import { scopedDbName } from '@/services/scopedDB'

/**
 * 키워드 분석 데이터 IndexedDB 관리 유틸리티
 */

class KeywordAnalysisDB {
  constructor() {
    this.dbName = 'KeywordAnalysisDB'
    this.version = 1
    this.db = null
  }

  // DB 초기화
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(scopedDbName(this.dbName), this.version)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        
        // 키워드 그룹별 분석 데이터 저장소
        if (!db.objectStoreNames.contains('keywordGroups')) {
          const store = db.createObjectStore('keywordGroups', { keyPath: 'groupId' })
          store.createIndex('groupName', 'groupName', { unique: false })
          store.createIndex('lastUpdated', 'lastUpdated', { unique: false })
        }
        
        // 개별 키워드 분석 결과 저장소
        if (!db.objectStoreNames.contains('keywordResults')) {
          const store = db.createObjectStore('keywordResults', { keyPath: 'id' })
          store.createIndex('groupId', 'groupId', { unique: false })
          store.createIndex('keyword', 'keyword', { unique: false })
          store.createIndex('vendorItemId', 'vendorItemId', { unique: false })
        }
      }
    })
  }

  // 키워드 그룹 데이터 저장
  async saveKeywordGroup(groupId, groupData) {
    if (!this.db) await this.init()

    const transaction = this.db.transaction(['keywordGroups'], 'readwrite')
    const store = transaction.objectStore('keywordGroups')

    // Vue 반응성 객체를 일반 객체로 변환 (깊은 복사)
    const cleanData = JSON.parse(JSON.stringify(groupData))

    const data = {
      groupId,
      ...cleanData,
      lastUpdated: new Date().toISOString()
    }

    console.log('🔧 [IndexedDB 저장] 데이터 정리 완료:', {
      groupId,
      analysisResultsLength: data.analysisResults?.length || 0,
      autoListLength: data.autoList?.length || 0
    })

    return new Promise((resolve, reject) => {
      const request = store.put(data)
      request.onsuccess = () => {
        console.log('✅ [IndexedDB 저장] 성공:', groupId)
        resolve(request.result)
      }
      request.onerror = () => {
        console.error('❌ [IndexedDB 저장] 실패:', request.error)
        reject(request.error)
      }
    })
  }

  // 키워드 그룹 데이터 불러오기
  async getKeywordGroup(groupId) {
    if (!this.db) await this.init()
    
    const transaction = this.db.transaction(['keywordGroups'], 'readonly')
    const store = transaction.objectStore('keywordGroups')
    
    return new Promise((resolve, reject) => {
      const request = store.get(groupId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // 키워드 분석 결과 저장 (개별 상품)
  async saveKeywordResult(groupId, keyword, productData) {
    if (!this.db) await this.init()

    // vendorItemId가 없는 상품 데이터는 저장하지 않음
    if (!productData || !productData.vendorItemId) {
      console.warn('⚠️ vendorItemId가 없는 상품 데이터 스킵:', productData)
      return Promise.resolve()
    }

    const transaction = this.db.transaction(['keywordResults'], 'readwrite')
    const store = transaction.objectStore('keywordResults')

    const data = {
      id: `${groupId}_${keyword}_${productData.vendorItemId}`,
      groupId,
      keyword,
      vendorItemId: productData.vendorItemId,
      productData,
      createdAt: new Date().toISOString()
    }
    
    return new Promise((resolve, reject) => {
      const request = store.put(data)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // 키워드 그룹의 모든 분석 결과 불러오기
  async getKeywordResults(groupId) {
    if (!this.db) await this.init()
    
    const transaction = this.db.transaction(['keywordResults'], 'readonly')
    const store = transaction.objectStore('keywordResults')
    const index = store.index('groupId')
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(groupId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // 키워드 그룹의 모든 vendorItemId 목록 가져오기
  async getExistingVendorItemIds(groupId) {
    if (!this.db) await this.init()
    
    const results = await this.getKeywordResults(groupId)
    return results.map(result => result.vendorItemId)
  }

  // 특정 키워드의 데이터 삭제
  async clearKeywordData(groupId, keyword) {
    if (!this.db) await this.init()

    const transaction = this.db.transaction(['keywordResults'], 'readwrite')
    const store = transaction.objectStore('keywordResults')
    const index = store.index('groupId')

    return new Promise((resolve, reject) => {
      const request = index.openCursor(groupId)
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          const result = cursor.value
          // 해당 키워드의 결과만 삭제
          if (result.keyword === keyword) {
            cursor.delete()
          }
          cursor.continue()
        } else {
          resolve()
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  // 키워드 그룹 삭제
  async deleteKeywordGroup(groupId) {
    if (!this.db) await this.init()

    const transaction = this.db.transaction(['keywordGroups', 'keywordResults'], 'readwrite')

    // 그룹 데이터 삭제
    const groupStore = transaction.objectStore('keywordGroups')
    await new Promise((resolve, reject) => {
      const request = groupStore.delete(groupId)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })

    // 관련 분석 결과 삭제
    const resultStore = transaction.objectStore('keywordResults')
    const index = resultStore.index('groupId')

    return new Promise((resolve, reject) => {
      const request = index.openCursor(groupId)
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        } else {
          resolve()
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  // 모든 키워드 그룹 목록 가져오기
  async getAllKeywordGroups() {
    if (!this.db) await this.init()
    
    const transaction = this.db.transaction(['keywordGroups'], 'readonly')
    const store = transaction.objectStore('keywordGroups')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // 키워드별 분석 결과 통계
  async getKeywordStats(groupId) {
    const results = await this.getKeywordResults(groupId)
    
    const stats = {}
    results.forEach(result => {
      if (!stats[result.keyword]) {
        stats[result.keyword] = {
          keyword: result.keyword,
          productCount: 0,
          totalImpressions: 0,
          totalClicks: 0,
          totalGmv: 0
        }
      }
      
      const product = result.productData
      stats[result.keyword].productCount++
      stats[result.keyword].totalImpressions += parseInt(product.volume) || 0
      stats[result.keyword].totalClicks += parseInt(product.clicks) || 0
      stats[result.keyword].totalGmv += parseInt(product.gmv) || 0
    })
    
    return Object.values(stats)
  }

  // 그룹의 모든 분석 결과 삭제 (그룹 정보는 유지)
  async clearGroupAnalysisData(groupId) {
    if (!this.db) await this.init()

    const transaction = this.db.transaction(['keywordResults'], 'readwrite')
    const store = transaction.objectStore('keywordResults')
    const index = store.index('groupId')

    return new Promise((resolve, reject) => {
      const request = index.openCursor(groupId)
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        } else {
          resolve()
        }
      }
      request.onerror = () => reject(request.error)
    })
  }
}

// 싱글톤 인스턴스 생성
const keywordAnalysisDB = new KeywordAnalysisDB()

export default keywordAnalysisDB
